/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Circle, Box, HStack, Button, useToast, Text } from "@chakra-ui/react";
import { CheckCircleIcon, CloseIcon } from "@chakra-ui/icons";
import { DataTable } from "../../components/DataTable";
import { getColumnsTelemetri } from "../../components/DataTable/columnsTelemetri";
import { sortTableData } from "../../utils/sort";
import TelemetriContextMenu from "../../components/ContextMenu/TelemetriContextMenu"; 
import GelenDataModal from "../../components/Modals/Telemetri/GelenDataModal";
import GelenUetdsDataModal from "../../components/Modals/Telemetri/GelenUetdsDataModal";
import SerbestKomutModal from "components/Modals/Telemetri/SerbestKomutModal";
import KmSetModal from "components/Modals/Telemetri/KmSetModal";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";
import { FiRefreshCw, FiDownload } from "react-icons/fi";
import { useFirmOptions } from "hooks/useFirmOptions";
import { useBayiOptions } from "hooks/useBayiOptions";
import { useUserRole } from "hooks/useUserRole";
import { useColumnOrder } from "hooks/useColumnOrder";
import { useFiltersRef } from "hooks/useFiltersRef";
import FirmSelectorWithFilters from "components/FirmSelectorWithFilters/FirmSelectorWithFilters";
import { formatFirmaName } from "utils/formatFirmaName";

// Sabit protokol listesi - liste yüklenmeden de arama yapılabilmesi için
const PROTOCOL_OPTIONS = [
  { label: "Teltonika", value: "teltonika" },
  { label: "XTAKIP", value: "xtakip" },
  { label: "Armoli", value: "armoli" },
  { label: "GT06", value: "gt06" },
];

// Cihaz model listesi
const DEVICE_MODELS = [
  "Teltonika FMB920",
  "Teltonika FMB140",
  "Teltonika FMB120",
  "Armoli L300",
  "Armoli M300",
  "Armoli P300",
  "Ortem M12",
  "GS900",
];

export default function TelemetriYonetimi() {
  const toast = useToast();
  const { handleError } = useErrorHandler(); // Merkezi error handling
  
  // Role kontrolü - Tüm roller erişebilir (admin, bayi, teknik_eleman)
  // Bu sayfa zaten tüm roller için açık
  const [telemetri, setTelemetri] = useState([]);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedFirmId, setSelectedFirmId] = useState("");
  const userRole = useUserRole();
  const {
    firms,
    loading: firmsLoading,
    error: firmsError,
  } = useFirmOptions();
  // Bayiler hook'u sadece admin ve teknik_eleman için aktif (yetkili bayi ve saas bayi göremez)
  const shouldLoadBayiler = userRole === "admin" || userRole === "teknik_eleman";
  const {
    bayiler,
    loading: bayilerLoading,
  } = useBayiOptions(shouldLoadBayiler);
  // Uygulanan firma ID'si (Listele butonuna basıldığında güncellenir)
  const [appliedSelectedFirmId, setAppliedSelectedFirmId] = useState("");
  
  const appliedSelectedFirm = useMemo(
    () =>
      appliedSelectedFirmId && appliedSelectedFirmId !== "all"
        ? firms.find((firma) => String(firma.id) === String(appliedSelectedFirmId))
        : null,
    [firms, appliedSelectedFirmId]
  );

  const [context, setContext] = useState({ position: null, row: null });
  const [selectedImei, setSelectedImei] = useState(null);
  const [selectedDeviceData, setSelectedDeviceData] = useState(null);
  const [isKomutModalOpen, setIsKomutModalOpen] = useState(false);
  const [selectedAracForKomut, setSelectedAracForKomut] = useState(null);
  const [isKmSetModalOpen, setIsKmSetModalOpen] = useState(false);
  const [selectedAracForKmSet, setSelectedAracForKmSet] = useState(null);
  const [uetdsLogDeviceRow, setUetdsLogDeviceRow] = useState(null);
  
  // Filtre state (kullanıcının yazdığı/seçtiği değerler)
  const [plakaFilter, setPlakaFilter] = useState("");
  const [imeiFilter, setImeiFilter] = useState("");
  const [simNoFilter, setSimNoFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [kontakFilter, setKontakFilter] = useState("");
  const [protocolFilter, setProtocolFilter] = useState("");
  const [cihazTipiFilter, setCihazTipiFilter] = useState("");
  const [bayiFilter, setBayiFilter] = useState("");
  
  // Uygulanan filtreler (Listele butonuna basınca bu değerler kullanılacak)
  const [appliedPlakaFilter, setAppliedPlakaFilter] = useState("");
  const [appliedImeiFilter, setAppliedImeiFilter] = useState("");
  const [appliedSimNoFilter, setAppliedSimNoFilter] = useState("");
  const [appliedStatusFilter, setAppliedStatusFilter] = useState("");
  const [appliedKontakFilter, setAppliedKontakFilter] = useState("");
  const [appliedProtocolFilter, setAppliedProtocolFilter] = useState("");
  const [appliedCihazTipiFilter, setAppliedCihazTipiFilter] = useState("");
  const [appliedBayiFilter, setAppliedBayiFilter] = useState("");
  const [uetdsFilter, setUetdsFilter] = useState("");
  const [appliedUetdsFilter, setAppliedUetdsFilter] = useState("");
  
  // Arama yapılıp yapılmadığını takip et
  const [hasSearched, setHasSearched] = useState(false);

  // Filtreleri ref ile tut - closure sorununu çözmek için
  const filtersRef = useFiltersRef({ plakaFilter, imeiFilter, simNoFilter, statusFilter, kontakFilter, protocolFilter, cihazTipiFilter, bayiFilter, uetdsFilter, selectedFirmId });

  // Role göre kolonları filtrele
  const columns = useMemo(() => {
    return getColumnsTelemetri(userRole);
  }, [userRole]);
  const [columnOrder, setColumnOrder] = useColumnOrder(columns);

  const loadTelemetri = useCallback((applyFilters = false, bayiFilterOverride = null) => {
    // Eğer filtreler uygulanacaksa (Listele butonuna basınca)
    if (applyFilters) {
      // Ref'ten güncel filtreleri al
      const currentFilters = filtersRef.current;
      
      // Firma seçimi özel durumu: "Hepsi" seçildiğinde (value="all") bu geçerli bir filtre olarak kabul edilir
      // Böylece tüm firmaları listeleyebiliriz
      const firmaSecildi = currentFilters.selectedFirmId && currentFilters.selectedFirmId !== "";
      
      // En az bir filtre seçilmiş olmalı (firma seçimi "Hepsi" olsa bile geçerli)
      const hasAnyFilter = 
        firmaSecildi || // Firma seçimi yapıldıysa (Hepsi dahil) geçerli
        (currentFilters.plakaFilter && currentFilters.plakaFilter.trim()) ||
        (currentFilters.imeiFilter && currentFilters.imeiFilter.trim()) ||
        (currentFilters.simNoFilter && currentFilters.simNoFilter.trim()) ||
        (currentFilters.statusFilter && currentFilters.statusFilter.trim()) ||
        (currentFilters.kontakFilter && currentFilters.kontakFilter.trim()) ||
        (currentFilters.protocolFilter && currentFilters.protocolFilter.trim()) ||
        (currentFilters.cihazTipiFilter && currentFilters.cihazTipiFilter.trim()) ||
        (currentFilters.bayiFilter && currentFilters.bayiFilter.trim()) ||
        (currentFilters.uetdsFilter && String(currentFilters.uetdsFilter).trim());
      
      if (!hasAnyFilter) {
        toast({
          title: "Uyarı",
          description: "Lütfen en az bir filtre seçin.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      setAppliedPlakaFilter(currentFilters.plakaFilter);
      setAppliedImeiFilter(currentFilters.imeiFilter);
      setAppliedSimNoFilter(currentFilters.simNoFilter);
      setAppliedStatusFilter(currentFilters.statusFilter);
      setAppliedKontakFilter(currentFilters.kontakFilter);
      setAppliedProtocolFilter(currentFilters.protocolFilter);
      setAppliedCihazTipiFilter(currentFilters.cihazTipiFilter);
      setAppliedBayiFilter(currentFilters.bayiFilter);
      setAppliedUetdsFilter(currentFilters.uetdsFilter);
      setAppliedSelectedFirmId(filtersRef.current.selectedFirmId); // Firma ID'sini de uygula
      setHasSearched(true);
    }
    
    // Firma seçilmeden de filtreleme yapılabilir - tüm telemetri verilerini getir
    setLoading(true);
    
    // ✅ Backend'e gönderilecek filtreleri belirle
    // Yenile butonunda mevcut uygulanan filtreleri kullan, Listele butonunda yeni filtreleri kullan
    const filtersToUse = applyFilters ? filtersRef.current : {
      bayiFilter: appliedBayiFilter,
      selectedFirmId: appliedSelectedFirmId,
      plakaFilter: appliedPlakaFilter,
      imeiFilter: appliedImeiFilter,
      simNoFilter: appliedSimNoFilter,
      cihazTipiFilter: appliedCihazTipiFilter,
      protocolFilter: appliedProtocolFilter,
      statusFilter: appliedStatusFilter,
      kontakFilter: appliedKontakFilter,
      uetdsFilter: appliedUetdsFilter,
    };
    
    // Bayi filtresi override (yenile butonu için)
    const bayiFilterToUse = bayiFilterOverride !== null 
      ? bayiFilterOverride 
      : filtersToUse.bayiFilter;
    
    // ✅ Tüm filtreleri backend'e gönder (DB seviyesinde filtreleme için)
    const params = new URLSearchParams();
    
    // Bayi filtresi
    if (bayiFilterToUse && (userRole === "admin" || userRole === "teknik_eleman")) {
      if (bayiFilterToUse === "ixpilot") {
        params.append("bayi_id", "null");
      } else {
        params.append("bayi_id", bayiFilterToUse);
      }
    }
    
    // Firma filtresi
    if (filtersToUse.selectedFirmId && filtersToUse.selectedFirmId !== "all") {
      params.append("firma_id", filtersToUse.selectedFirmId);
    }
    
    // Plaka filtresi
    if (filtersToUse.plakaFilter && filtersToUse.plakaFilter.trim()) {
      params.append("plaka", filtersToUse.plakaFilter.trim());
    }
    
    // IMEI filtresi
    if (filtersToUse.imeiFilter && filtersToUse.imeiFilter.trim()) {
      params.append("imei", filtersToUse.imeiFilter.trim());
    }
    
    // SIM No filtresi
    if (filtersToUse.simNoFilter && filtersToUse.simNoFilter.trim()) {
      params.append("sim_no", filtersToUse.simNoFilter.trim());
    }
    
    // Cihaz Tipi filtresi
    if (filtersToUse.cihazTipiFilter && filtersToUse.cihazTipiFilter.trim()) {
      params.append("cihaz_tipi", filtersToUse.cihazTipiFilter.trim());
    }
    
    // Protokol filtresi (backend'de filtreleme için)
    // Not: Status filtresi (aktif/pasif) frontend'de kalıyor çünkü Traccar'dan gelen veriye bağlı
    if (filtersToUse.protocolFilter && filtersToUse.protocolFilter.trim()) {
      params.append("protokol", filtersToUse.protocolFilter.trim());
    }
    if (filtersToUse.statusFilter && filtersToUse.statusFilter.trim()) {
      params.append("status", filtersToUse.statusFilter.trim());
    }
    if (filtersToUse.kontakFilter && filtersToUse.kontakFilter.trim()) {
      params.append("kontak", filtersToUse.kontakFilter.trim());
    }
    const uetdsChoice =
      typeof filtersToUse.uetdsFilter === "string"
        ? filtersToUse.uetdsFilter
        : "";
    if (uetdsChoice === "acik") {
      params.append("uetds", "true");
    } else if (uetdsChoice === "kapali") {
      params.append("uetds", "false");
    }
    
    const url = `/api/telemetri/${params.toString() ? `?${params.toString()}` : ""}`;
    api
      .get(url)
      .then((res) => res.json())
      .then((data) => setTelemetri(Array.isArray(data) ? data : []))
      .catch(() => setTelemetri([]))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userRole,
    appliedBayiFilter,
    appliedSelectedFirmId,
    appliedPlakaFilter,
    appliedImeiFilter,
    appliedSimNoFilter,
    appliedCihazTipiFilter,
    appliedProtocolFilter,
    appliedStatusFilter,
    appliedKontakFilter,
    appliedUetdsFilter,
  ]);

  // Firma seçimi kaldırıldığında listeyi temizle (otomatik yükleme yok)
  useEffect(() => {
    if (!selectedFirmId) {
      // Firma seçimi kaldırıldığında listeyi temizle
      setTelemetri([]);
      setLoading(false);
      setHasSearched(false);
    }
  }, [selectedFirmId]);

  const filteredRows = useMemo(() => {
    // Tüm filtreler (durum, kontak dahil) backend'de uygulanır; burada sadece görüntüleme alanları hazırlanır.

    const parseSonAktiflik = (value) => {
      if (!value) return null;
      const [datePart, timePart] = value.split(" ");
      if (!datePart || !timePart) return null;
      const [day, month, year] = datePart.split("/").map(Number);
      if (!day || !month || !year) return null;
      const padded = (num) => String(num).padStart(2, "0");
      const iso = `${year}-${padded(month)}-${padded(day)}T${timePart}`;
      const parsed = new Date(iso);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    // Tarih/saat formatını "saat tarih" formatına çevir ve güzel göster
    const formatDateTime = (value) => {
      if (!value || value === "-") return <Text color="gray.400">-</Text>;
      
      const date = parseSonAktiflik(value) || new Date(value);
      if (!date || Number.isNaN(date.getTime())) return value;
      
      const pad = (n) => String(n).padStart(2, "0");
      const time = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
      const dateStr = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
      
      return `${time} ${dateStr}`;
    };

    return telemetri.map((r) => {
        const now = Date.now();
        // Son gelen log'un zamanına bak (server_time - Traccar sunucusunun logu aldığı zaman)
        // Log içeriği ne olursa olsun, sunucunun logu ne zaman aldığına bakıyoruz
        let lastLogTime = null;
        
        // Önce server_time'a bak (Traccar sunucusunun logu aldığı gerçek zaman)
        if (r.server_time) {
          try {
            lastLogTime = new Date(r.server_time);
          } catch (e) {
            // Parse edilemezse son_aktiflik'e bak
          }
        }
        
        // server_time yoksa veya parse edilemediyse son_aktiflik'e bak
        if (!lastLogTime || isNaN(lastLogTime.getTime())) {
          lastLogTime = parseSonAktiflik(r.son_aktiflik);
        }
        
        // Son 5 dakika içinde log geldiyse yeşil, gelmediyse kırmızı
        const aktifMi =
          lastLogTime && !isNaN(lastLogTime.getTime()) && now - lastLogTime.getTime() <= 5 * 60 * 1000;

        // Kontak ham değeri
        let ignitionAcik =
          r.ignition === "Açık" ||
          r.ignition === "On" ||
          r.ignition === 1 ||
          r.ignition === true;

        // Koruma: son telemetri 5 dakikadan eskiyse kontağı da kapalı kabul et
        const FORCE_OFF_MS = 5 * 60 * 1000;
        if (
          ignitionAcik &&
          (!lastLogTime || isNaN(lastLogTime.getTime()) || now - lastLogTime.getTime() >= FORCE_OFF_MS)
        ) {
          ignitionAcik = false;
        }

        // Sıralama için ham timestamp'leri hesapla (sadece sıralama için, görüntüleme değişmeyecek)
        let sonAktiflikTimestamp = null;
        if (r.server_time) {
          try {
            const dt = new Date(r.server_time);
            if (!isNaN(dt.getTime())) sonAktiflikTimestamp = dt.getTime();
          } catch (e) {}
        }
        if (!sonAktiflikTimestamp && r.device_time) {
          try {
            const dt = new Date(r.device_time);
            if (!isNaN(dt.getTime())) sonAktiflikTimestamp = dt.getTime();
          } catch (e) {}
        }
        if (!sonAktiflikTimestamp && r.son_aktiflik) {
          const parsed = parseSonAktiflik(r.son_aktiflik);
          if (parsed) sonAktiflikTimestamp = parsed.getTime();
        }

        let sonKonumTimestamp = null;
        if (r.server_time) {
          try {
            const dt = new Date(r.server_time);
            if (!isNaN(dt.getTime())) sonKonumTimestamp = dt.getTime();
          } catch (e) {}
        }
        if (!sonKonumTimestamp && r.device_time) {
          try {
            const dt = new Date(r.device_time);
            if (!isNaN(dt.getTime())) sonKonumTimestamp = dt.getTime();
          } catch (e) {}
        }
        if (!sonKonumTimestamp && r.son_konum_zamani) {
          const parsed = parseSonAktiflik(r.son_konum_zamani);
          if (parsed) sonKonumTimestamp = parsed.getTime();
        }

        return {
          ...r,
          firma: r.firma ? formatFirmaName(r.firma) : r.firma,
          uetds:
            r.uetds === true ? (
              <CheckCircleIcon color="green.500" boxSize={5} />
            ) : r.uetds === false ? (
              <CloseIcon color="red.500" boxSize={4} />
            ) : (
              <Text fontSize="xs" color="gray.500">
                Veri yok
              </Text>
            ),
          uetds_son_gonderim_zamani: formatDateTime(r.uetds_son_gonderim_zamani),
          aktiflik: (
            <Circle
              size="12px"
              bg={aktifMi ? "green.400" : "red.400"}
              display="inline-block"
            />
          ),
          ignition: (
            <Circle
              size="12px"
              bg={ignitionAcik ? "green.400" : "red.400"}
              display="inline-block"
            />
          ),
          son_aktiflik: formatDateTime(r.son_aktiflik),
          son_konum_zamani: formatDateTime(r.son_konum_zamani),
          sonGuncelleme: r.son_guncelleme ?? "-",
          // Sıralama için ham timestamp'ler (gizli alanlar, sadece sıralama için)
          _son_aktiflik_timestamp: sonAktiflikTimestamp,
          _son_konum_timestamp: sonKonumTimestamp,
        };
      });
  }, [telemetri]);

  const filterFields = [
    ...(userRole === "admin" || userRole === "teknik_eleman" ? [{
      key: "bayi",
      type: "select",
      label: "Bayi",
      placeholder: "Bayi Seçiniz",
      value: bayiFilter,
      onChange: setBayiFilter,
      options: [
        { label: "Tümü", value: "" },
        { label: "ixPilot", value: "ixpilot" },
        ...bayiler.map((bayi) => ({
          label: bayi.name,
          value: bayi.id,
        })),
      ],
      isDisabled: bayilerLoading,
    }] : []),
    {
      key: "plaka",
      type: "text",
      label: "Plaka",
      placeholder: "Plaka",
      value: plakaFilter,
      onChange: setPlakaFilter,
    },
    {
      key: "imei",
      type: "text",
      label: "IMEI",
      placeholder: "IMEI",
      value: imeiFilter,
      onChange: setImeiFilter,
    },
    {
      key: "sim_no",
      type: "text",
      label: "SIM No",
      placeholder: "SIM No",
      value: simNoFilter,
      onChange: setSimNoFilter,
    },
    {
      key: "status",
      type: "select",
      label: "Cihaz Durumu",
      placeholder: "Cihaz Durumu",
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { label: "Aktif", value: "aktif" },
        { label: "Pasif", value: "pasif" },
      ],
    },
    {
      key: "kontak",
      type: "select",
      label: "Kontak",
      placeholder: "Kontak",
      value: kontakFilter,
      onChange: setKontakFilter,
      options: [
        { label: "Açık", value: "açık" },
        { label: "Kapalı", value: "kapalı" },
      ],
    },
    {
      key: "protocol",
      type: "select",
      label: "Protokol",
      placeholder: "Protokol",
      value: protocolFilter,
      onChange: setProtocolFilter,
      options: PROTOCOL_OPTIONS,
    },
    {
      key: "cihaz_tipi",
      type: "select",
      label: "Cihaz Tipi",
      placeholder: "Cihaz Tipi Seçiniz",
      value: cihazTipiFilter,
      onChange: setCihazTipiFilter,
      options: [
        { label: "Tümü", value: "" },
        ...DEVICE_MODELS.map((model) => ({
          label: model,
          value: model,
        })),
      ],
    },
    {
      key: "uetds",
      type: "select",
      label: "UETDS",
      placeholder: "UETDS",
      value: uetdsFilter,
      onChange: setUetdsFilter,
      options: [
        { label: "Tümü", value: "" },
        { label: "Açık", value: "acik" },
        { label: "Kapalı", value: "kapali" },
      ],
    },
  ];


  // Sadece son_aktiflik ve son_konum_zamani için özel sıralama (tarih+saat birlikte)
  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredRows;

    // Son aktiflik veya son konum zamanı sıralanıyorsa özel sıralama yap
    if (sortColumn === "son_aktiflik" || sortColumn === "son_konum_zamani") {
      const timestampKey = sortColumn === "son_aktiflik" ? "_son_aktiflik_timestamp" : "_son_konum_timestamp";
      
      return [...filteredRows].sort((a, b) => {
        const timestampA = a[timestampKey];
        const timestampB = b[timestampKey];
        
        // Geçersiz timestamp'ler en sona
        if (!timestampA && !timestampB) return 0;
        if (!timestampA) return 1;
        if (!timestampB) return -1;
        
        // Tarih+saat kontrolü ile sırala
        return sortOrder === "asc" 
          ? timestampA - timestampB 
          : timestampB - timestampA;
      });
    }

    // Diğer kolonlar için mevcut sıralama fonksiyonunu kullan
    return sortTableData(filteredRows, sortColumn, sortOrder);
  }, [filteredRows, sortColumn, sortOrder]);

  const handleSort = (col) => {
    if (sortColumn === col) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else {
      setSortColumn(col);
      setSortOrder("asc");
    }
  };

  const handleContextMenu = (e, row) => {
    e.preventDefault();
    setContext({ position: { x: e.clientX, y: e.clientY }, row });
  };

  const closeMenu = () => setContext({ position: null, row: null });

  const handleGatherSimForDevice = async (deviceData) => {
    if (!deviceData?.id && !deviceData?.cihaz_id) {
      const deviceId = deviceData?.cihaz_id || deviceData?.id;
      if (!deviceId) return;
    }
    closeMenu();
    const deviceId = deviceData?.cihaz_id || deviceData?.id;
    try {
      const res = await api.post(`/api/sim-kartlari/gather/${deviceId}`);
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data?.detail || "SIM toplama başlatılamadı");
      }
      toast({
        title: "Başarılı",
        description: data.message || "SIM numarası toplama işlemi başlatıldı",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      // Liste yenileme kaldırıldı - SIM toplama işlemi arka planda çalışıyor
    } catch (err) {
      toast({
        title: "Hata",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleDownloadPDF = async () => {
    // Listeleme yapılmadan PDF indirilemesin
    if (!hasSearched) {
      toast({
        title: "Uyarı",
        description: "Önce 'Listele' butonuna basarak telemetri verilerini listeleyin.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Query parametrelerini oluştur
      const params = new URLSearchParams();
      if (appliedSelectedFirmId && appliedSelectedFirmId !== "all") {
        params.append("firma_id", appliedSelectedFirmId);
      }
      // Bayi filtresi (sadece admin ve teknik_eleman için)
      const currentBayiFilter = appliedBayiFilter || bayiFilter;
      if (currentBayiFilter && (userRole === "admin" || userRole === "teknik_eleman")) {
        // "ixpilot" seçildiğinde özel bir değer gönder (backend'de null olarak yorumlanacak)
        if (currentBayiFilter === "ixpilot") {
          params.append("bayi_id", "null");
        } else {
          params.append("bayi_id", currentBayiFilter);
        }
      }
      // Diğer filtreler
      if (appliedPlakaFilter) {
        params.append("plaka", appliedPlakaFilter);
      }
      if (appliedImeiFilter) {
        params.append("imei", appliedImeiFilter);
      }
      if (appliedSimNoFilter) {
        params.append("sim_no", appliedSimNoFilter);
      }
      if (appliedCihazTipiFilter) {
        params.append("cihaz_tipi", appliedCihazTipiFilter);
      }
      if (appliedProtocolFilter) {
        params.append("protokol", appliedProtocolFilter);
      }
      if (appliedStatusFilter?.trim()) {
        params.append("status", appliedStatusFilter.trim());
      }
      if (appliedKontakFilter?.trim()) {
        params.append("kontak", appliedKontakFilter.trim());
      }
      if (appliedUetdsFilter === "acik") params.append("uetds", "true");
      if (appliedUetdsFilter === "kapali") params.append("uetds", "false");
      
      const queryString = params.toString();
      const url = `/api/telemetri/pdf${queryString ? `?${queryString}` : ""}`;
      
      // PDF'i indir - apiFetch kullanarak blob response al
      const { apiFetch } = await import("utils/api");
      const response = await apiFetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("PDF oluşturulamadı");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      
      // Dosya adı
      let filename = "telemetri_raporu.pdf";
      if (appliedSelectedFirm && appliedSelectedFirm.name) {
        filename = `telemetri_raporu_${appliedSelectedFirm.name.replace(/\s+/g, '_')}.pdf`;
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: "PDF indirildi",
        description: "Telemetri raporu başarıyla indirildi.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(error, "PDF indirme hatası");
    }
  };

  const handleDownloadExcel = async () => {
    if (!hasSearched) {
      toast({
        title: "Uyarı",
        description: "Önce 'Listele' butonuna basarak telemetri verilerini listeleyin.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setExcelLoading(true);
      const params = new URLSearchParams();
      if (appliedSelectedFirmId && appliedSelectedFirmId !== "all") {
        params.append("firma_id", appliedSelectedFirmId);
      }
      const currentBayiFilter = appliedBayiFilter || bayiFilter;
      if (currentBayiFilter && (userRole === "admin" || userRole === "teknik_eleman")) {
        if (currentBayiFilter === "ixpilot") {
          params.append("bayi_id", "null");
        } else {
          params.append("bayi_id", currentBayiFilter);
        }
      }
      if (appliedPlakaFilter) {
        params.append("plaka", appliedPlakaFilter);
      }
      if (appliedImeiFilter) {
        params.append("imei", appliedImeiFilter);
      }
      if (appliedSimNoFilter) {
        params.append("sim_no", appliedSimNoFilter);
      }
      if (appliedCihazTipiFilter) {
        params.append("cihaz_tipi", appliedCihazTipiFilter);
      }
      if (appliedProtocolFilter) {
        params.append("protokol", appliedProtocolFilter);
      }
      if (appliedStatusFilter?.trim()) {
        params.append("status", appliedStatusFilter.trim());
      }
      if (appliedKontakFilter?.trim()) {
        params.append("kontak", appliedKontakFilter.trim());
      }
      if (appliedUetdsFilter === "acik") params.append("uetds", "true");
      if (appliedUetdsFilter === "kapali") params.append("uetds", "false");

      const queryString = params.toString();
      const url = `/api/telemetri/excel${queryString ? `?${queryString}` : ""}`;
      const { apiFetch } = await import("utils/api");
      const response = await apiFetch(url, { method: "GET" });
      if (!response.ok) {
        throw new Error("Excel oluşturulamadı");
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      let filename = "telemetri_raporu.xlsx";
      if (appliedSelectedFirm && appliedSelectedFirm.name) {
        filename = `telemetri_raporu_${appliedSelectedFirm.name.replace(/\s+/g, "_")}.xlsx`;
      }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: "Excel indirildi",
        description: "Telemetri raporu başarıyla indirildi.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      handleError(error, "Excel indirme hatası");
    } finally {
      setExcelLoading(false);
    }
  };

  return (
    <Box position="relative" mt={{ base: 2, md: 6 }}>
      <FirmSelectorWithFilters
        title="Telemetri"
        firms={firms}
        selectedFirmId={selectedFirmId}
        onChange={setSelectedFirmId}
        loading={firmsLoading}
        error={firmsError}
        includeAllOption
        fields={filterFields}
        onSearch={() => loadTelemetri(true)}
        searchButtonLoading={loading}
        searchButtonLoadingText="Listeleniyor..."
        onReset={() => {
          setPlakaFilter("");
          setImeiFilter("");
          setSimNoFilter("");
          setStatusFilter("");
          setKontakFilter("");
          setProtocolFilter("");
          setCihazTipiFilter("");
          setBayiFilter("");
          setAppliedPlakaFilter("");
          setAppliedImeiFilter("");
          setAppliedSimNoFilter("");
          setAppliedStatusFilter("");
          setAppliedKontakFilter("");
          setAppliedProtocolFilter("");
          setAppliedCihazTipiFilter("");
          setAppliedBayiFilter("");
          setUetdsFilter("");
          setAppliedUetdsFilter("");
          setAppliedSelectedFirmId("");
          setHasSearched(false);
          // Firma seçimini de sıfırla
          setSelectedFirmId("");
        }}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            loadTelemetri(true);
          }
        }}
      />

      <DataTable
        title={
          appliedSelectedFirm
            ? `Telemetri Yönetimi • ${appliedSelectedFirm.name} • ${sortedData.length} kayıt`
            : `Telemetri Yönetimi • ${sortedData.length} kayıt`
        }
        filterBar={
          <HStack justify="flex-end" spacing={3}>
            <Button
              size="sm"
              leftIcon={<FiDownload />}
              colorScheme="blue"
              variant="outline"
              onClick={handleDownloadPDF}
              isLoading={loading}
              loadingText="Hazırlanıyor"
              isDisabled={!hasSearched}
            >
              PDF İndir
            </Button>
            <Button
              size="sm"
              leftIcon={<FiDownload />}
              colorScheme="green"
              variant="outline"
              onClick={handleDownloadExcel}
              isLoading={excelLoading}
              loadingText="Hazırlanıyor"
              isDisabled={!hasSearched}
            >
              Excel İndir
            </Button>
            <Button
              size="sm"
              leftIcon={<FiRefreshCw />}
              variant="ghost"
              onClick={() => loadTelemetri(false, appliedBayiFilter)}
              isLoading={loading}
              loadingText="Yükleniyor"
              isDisabled={!hasSearched}
            >
              Yenile
            </Button>
          </HStack>
        }
        columns={columns}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
        data={sortedData}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onSort={handleSort}
        tableFontSize="9px"
        tableRowHeight="20px"
        loading={loading}
        hasSearched={hasSearched}
        emptyMessage={
          hasSearched
            ? "Arama kriterlerinize uygun kayıt bulunamadı."
            : "Henüz liste yüklenmedi. Firma seçin veya filtreleri doldurup 'Listele' butonuna basarak arama yapabilirsiniz."
        }
        onRowContextMenu={handleContextMenu}
      />

      {/* Sağ tık menüsü */}
      {context.position && context.row && (
        <TelemetriContextMenu
          position={context.position}
          data={context.row}
          onClose={closeMenu}
          showUetdsRawlogs={userRole === "admin"}
          onOpenGelenUetdsData={(row) => setUetdsLogDeviceRow(row)}
          onOpenGelenData={(imei, deviceData) => {
            setSelectedImei(imei);
            setSelectedDeviceData(deviceData);
          }}
          onOpenKomutModal={(arac) => {
            setSelectedAracForKomut(arac);
            setIsKomutModalOpen(true);
          }}
          onOpenKmSetModal={(arac) => {
            setSelectedAracForKmSet(arac);
            setIsKmSetModalOpen(true);
          }}
          onGatherSim={handleGatherSimForDevice}
        />
      )}

      {/* Gelen Data Modal */}
      {selectedImei && (
        <GelenDataModal 
          isOpen={!!selectedImei} 
          onClose={() => {
            setSelectedImei(null);
            setSelectedDeviceData(null);
          }} 
          imei={selectedImei}
          deviceData={selectedDeviceData}
        />
      )}

      {uetdsLogDeviceRow != null ? (
        <GelenUetdsDataModal
          isOpen={!!uetdsLogDeviceRow}
          deviceData={uetdsLogDeviceRow}
          onClose={() => setUetdsLogDeviceRow(null)}
        />
      ) : null}

      {/* Serbest Komut Modal */}
      <SerbestKomutModal
        isOpen={isKomutModalOpen}
        onClose={() => {
          setIsKomutModalOpen(false);
          setSelectedAracForKomut(null);
        }}
        aracData={selectedAracForKomut}
      />

      <KmSetModal
        isOpen={isKmSetModalOpen}
        onClose={() => {
          setIsKmSetModalOpen(false);
          setSelectedAracForKmSet(null);
        }}
        aracData={selectedAracForKmSet}
        onSuccess={() => loadTelemetri(false, appliedBayiFilter)}
      />
    </Box>
  );
}
